import React, { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { UserEntity } from '@data';
import { type ProductPageTab, productReportingPath, productTabPath } from '@app/constants/url';
import { useOrganizationId } from '@app/utils/hooks';
import ProductDetail from './productDetail';
import ProductsList from './productsList';

export interface ProductsProps {
  user: UserEntity;
}

const TABS: ProductPageTab[] = [
  'settings',
  'team',
  'sprints',
  'incidents',
  'costs',
  'monitoring',
  'quality',
  'productivity',
  'security-audit',
];
// Tabs an old link may still name, under the name they have now.
const RENAMED_TABS = new Map<string | undefined, ProductPageTab>([
  ['reports', 'sprints'],
  ['health', 'monitoring'],
  ['spend', 'costs'],
  ['budget', 'costs'],
]);

/** The page the client's view used to be, now the product page itself: an old link lands on the tab it named. */
const ClientViewRedirect = (): ReactElement => {
  const { productId = '', tab } = useParams<{ productId: string; tab?: string }>();
  const organizationId = useOrganizationId() ?? '';
  const named = RENAMED_TABS.get(tab) ?? TABS.find(each => each === tab);
  const path = productTabPath(organizationId, productId, named ?? 'dashboard');
  // Budget is a view of Costs now, which opens on Billed unless told otherwise.
  return <Navigate to={tab === 'budget' ? `${path}?view=budgeted` : path} replace />;
};

/** Monitoring was Health and Costs was Spend: an old link lands on the tab under its new name, arrow state and all. */
const RenamedTabRedirect = ({ to, view }: { to: ProductPageTab; view?: string }): ReactElement => {
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId() ?? '';
  const { state } = useLocation();
  const path = productTabPath(organizationId, productId, to);
  return <Navigate to={view ? `${path}?view=${view}` : path} replace state={state} />;
};

/** Dashboard and Insights were tabs of their own before the dashboard became the product's front page. */
const DashboardRedirect = (): ReactElement => {
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId() ?? '';
  return <Navigate to={productTabPath(organizationId, productId, 'dashboard')} replace />;
};

/** The sprint as the client read it, from before a sprint opened on the page we write and close it on. */
const ClientViewSprintRedirect = (): ReactElement => {
  const { productId = '', sprintId = '' } = useParams<{ productId: string; sprintId: string }>();
  const organizationId = useOrganizationId() ?? '';
  return <Navigate to={productReportingPath(organizationId, productId, sprintId)} replace />;
};

const ProductsRouter = ({ user }: ProductsProps): ReactElement => (
  <Routes>
    <Route path=":productId" element={<ProductDetail />} />
    {/* The tab is its own route, so a deep link and a refresh both land on it. */}
    {TABS.map(tab => (
      <Route key={tab} path={`:productId/${tab}`} element={<ProductDetail tab={tab} />} />
    ))}
    <Route path=":productId/health" element={<RenamedTabRedirect to="monitoring" />} />
    <Route path=":productId/spend" element={<RenamedTabRedirect to="costs" />} />
    <Route path=":productId/budget" element={<RenamedTabRedirect to="costs" view="budgeted" />} />
    <Route path=":productId/dashboard" element={<DashboardRedirect />} />
    <Route path=":productId/insights" element={<DashboardRedirect />} />
    <Route path=":productId/client-view/sprints/:sprintId" element={<ClientViewSprintRedirect />} />
    <Route path=":productId/client-view" element={<ClientViewRedirect />} />
    <Route path=":productId/client-view/:tab" element={<ClientViewRedirect />} />
    <Route path="*" element={<ProductsList user={user} />} />
  </Routes>
);

export default ProductsRouter;
